import { useAsync } from 'hooks';
import { uploadFileToS3 } from 'logic/uploadFileToS3';
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

require('./ProgressiveUploadButton.css');

export type Props = {
    id?: string;
    className?: string;
    size?: 'large' | 'medium' | '' | 'small' | 'tiny';
    accept?: string;
    disabled?: boolean;
    progress?: number;
    errorMessage?: string;

    // This function supplies an upload URL for the given file.
    // (the client needs to request an upload URL from MosaicONE)
    urlFunc(file: File): Promise<string>;

    onUploadDone(file: File): void;
    onUploadFailed?(): void;
};

const eatEvent = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
};

const noop = () => {};

export const ProgressiveUploadButton = ({
    id,
    className,
    size = '',
    accept,
    progress = 0,
    disabled = false,
    errorMessage: initialErrorMessage = '',
    urlFunc,
    onUploadDone,
    onUploadFailed = noop,
}: Props) => {
    const { t } = useTranslation();
    const { isMounted } = useAsync();

    const [ uploadProgress, setUploadProgress ] = useState(progress);
    const [ errorMessage, setErrorMessage ] = useState<string | null>(initialErrorMessage);
    const [ inProgress, setInProgress ] = useState(progress > 0);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClickUpload = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const { current } = fileInputRef;
        if (current) {
            current.click();
        }
    }, [ fileInputRef ]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setUploadProgress(0);
        const file: File | null = e.target && e.target.files && e.target.files[0];
        if (!file) {
            return;
        }
        setErrorMessage(null);
        setInProgress(true);
                    
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.addEventListener('load', e => {
            const { target } = e;
            if (!target?.result) {
                isMounted() && setErrorMessage(t('ERROR.UPLOAD_FAILED'));
                onUploadFailed();
            }

            void (async () => {
                let uploadUrl: string;
                try {
                    uploadUrl = await urlFunc(file);
                }
                catch (err) {
                    console.error('Failed to get upload URL', err);
                    isMounted() && setErrorMessage(t('ERROR.UPLOAD_FAILED'));
                    isMounted() && setInProgress(false);
                    onUploadFailed();
                    return;
                }

                try {
                    await uploadFileToS3(uploadUrl, file, progress => {
                        if (!isMounted()) return;
                        setUploadProgress(progress);
                    });
                }
                catch (err) {
                    console.error(`Failed to upload file "${file.name}"`, err);
                    isMounted() && setErrorMessage(t('ERROR.UPLOAD_FAILED'));
                    onUploadFailed();
                    return;
                }
                finally {
                    isMounted() && setInProgress(false);
                }

                onUploadDone(file);
            })();
        });
    }, [ isMounted, urlFunc, onUploadDone, onUploadFailed, setErrorMessage, setInProgress, setUploadProgress, t ]);

    // Turn off the progress indicators if we encountered an error
    useEffect(() => {
        if (errorMessage) {
            setUploadProgress(0);
            setInProgress(false);
        }
    }, [ errorMessage, setInProgress, setUploadProgress ]);

    return (
        <span className={ `progressive-upload-button ${className || ''}` }>
            { !inProgress &&
                <button type="button"
                    className={ `ui ${size || ''} white button _upload-button` }
                    onClick={ handleClickUpload }
                    onMouseDown={ eatEvent }
                    onMouseUp={ eatEvent }
                    disabled={ disabled || inProgress }
                >
                    { t('COMMAND.UPLOAD') }
                </button>
            }
            { inProgress && !errorMessage &&
                <progress
                    max={ 100 }
                    value={ uploadProgress * 100 }
                    className="_progress-bar"
                />
            }
            { !inProgress && errorMessage &&
                <span className='_error-message'>
                    <i className="ui red exclamation triangle icon" />
                    { errorMessage }
                </span>
            }
            <input
                id={ id }
                type="file"
                ref={ fileInputRef }
                onChange={ handleInputChange }
                accept={ accept }
                className="_hidden-input"
            />
        </span>
    );
};
